import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Swiper as SwiperModule, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Swiper } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import { BREAKPOINTS } from '../../helpers/breakpoints';
import { DataLayerObject, ExtendedWindowType } from '../../helpers/global-types';

type ColorSwiperProps = {
  images: {
    media_url: string;
    alt_text: string;
    color_name?: string;
    color_id?: string;
  }[];
  nextLabel: string;
  prevLabel: string;
  selected: string;
  defaultColor: string;
  handleSelected: (val: string) => void;
  fullScreen?: boolean;
};

export const ColorSwiper = ({
  images,
  nextLabel,
  prevLabel,
  handleSelected,
  selected,
  defaultColor,
  fullScreen
}: ColorSwiperProps) => {
  const [, setSwiper] = useState<Swiper | null>(null);
  const [touch, setTouch] = useState(false);
  const [shouldPushLayer, setShouldPushLayer] = useState<Swiper | null>(null);
  const nextButton = useRef(null);
  const backButton = useRef(null);
  const sortedImages = useMemo(() => {
    const remainingImages = images.filter(
      image => image.color_id !== defaultColor
    );
    const defaultImage = images.find(image => image.color_id === defaultColor);
    const sortedImages = remainingImages.sort((a, b) => {
      if (a.color_name && b.color_name) {
        return a.color_name.localeCompare(b.color_name);
      }
      return 0;
    });

    if (defaultImage) {
      sortedImages.unshift(defaultImage);
    }
    return sortedImages;
  }, [images]);

  const initialSlide =
    sortedImages.findIndex(image => image.color_id === selected) > 0
      ? images.findIndex(image => image.color_id === selected)
      : 0;

  const handleSwiperLayerEvent = (swiper: Swiper) => {
    setShouldPushLayer(swiper);
  };

  useEffect(() => {
    if (shouldPushLayer) {
      const interactLayer = `{"event":"carousel_interaction","event_action":"${
        touch ? 'swipe' : 'arrow click'
      }","event_category":"content_interaction","event_label":"${(
        shouldPushLayer.activeIndex + 1
      ).toString()} of ${shouldPushLayer.slides.length}"}`;
      (window as ExtendedWindowType).dataLayer.push(
        JSON.parse(interactLayer) as DataLayerObject
      );
      setShouldPushLayer(null);
    }
  }, [shouldPushLayer, touch]);

  return (
    <>
      {selected && sortedImages && (
        <div className="color-swiper">
          {}
          <SwiperModule
            breakpoints={{
              768: {
                slidesPerView: fullScreen ? 5 : 6,
                spaceBetween: 30,
                slidesOffsetBefore: 0
              },
              1024: {
                slidesPerView: fullScreen ? 5 : 8,
                spaceBetween: 30,
                slidesOffsetBefore: 0
              },
              1400: {
                slidesPerView: fullScreen ? 5 : 10,
                spaceBetween: 30,
                slidesOffsetBefore: 0
              }
            }}
            spaceBetween={20}
            slidesPerView={!fullScreen ? 2.5 : 2}
            slidesOffsetBefore={!fullScreen ? 65 : 0}
            initialSlide={initialSlide}
            navigation={{
              nextEl: nextButton.current,
              prevEl: backButton.current
            }}
            modules={[FreeMode, Navigation]}
            onSwiper={swiper => setSwiper(swiper)}
            onSlideChange={swiper => handleSwiperLayerEvent(swiper)}
            onTouchEnd={() => setTouch(true)}
            onMouseUp={() => setTouch(false)}
          >
            {sortedImages.map(image => (
              <SwiperSlide key={image.color_id}>
                <div
                  className={`color-swiper__card ${
                    image.color_id === selected
                      ? 'color-swiper__card--selected'
                      : ''
                  }`}
                >
                  <picture className="color-swiper__img-wrapper">
                    <source
                      srcSet={`${image.media_url}?width=121`}
                      media={`(min-width: ${BREAKPOINTS.tablet - 1}px)`}
                    />
                    <img
                      className="color-swiper__img"
                      src={`${image.media_url}?width=263`}
                      alt={image.alt_text}
                      height="263"
                      width="263"
                      loading="lazy"
                    />
                  </picture>
                  <button
                    className="color-swiper__label link"
                    onClick={() => {
                      handleSelected(image.color_id as string);
                    }}
                  >
                    <span>{image.color_name}</span>
                  </button>
                </div>
              </SwiperSlide>
            ))}
          </SwiperModule>
          <button
            ref={backButton}
            className="swiper-button-prev"
            aria-label={prevLabel || 'previous slide'}
            title={prevLabel || 'previous slide'}
          ></button>
          <button
            ref={nextButton}
            className="swiper-button-next"
            aria-label={nextLabel || 'next slide'}
            title={nextLabel || 'next slide'}
          ></button>
        </div>
      )}
    </>
  );
};

export default ColorSwiper;
